import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useHistory, Link } from 'react-router-dom';
import '../../Styles/redesign.css';

interface ModalData {
  message: string;
  detail?: string;
}

interface RedesignContextType {
  openModal: (message: string, detail?: string) => void;
  closeModal: () => void;
  darkMode: boolean;
  toggleDarkMode: () => void;
}

const RedesignContext = createContext<RedesignContextType>({
  openModal: () => {},
  closeModal: () => {},
  darkMode: false,
  toggleDarkMode: () => {},
});

export const useRedesign = () => useContext(RedesignContext);

interface Subpage {
  path: string;
  title: string;
}

interface NavGroup {
  id: string;
  name: string;
  start: string;
  subpages: Subpage[];
}

export const NAV_GROUPS: NavGroup[] = [
  { id: 'home', name: '首頁', start: '/', subpages: [] },
  {
    id: 'vc',
    name: '數位憑證',
    start: '/vc',
    subpages: [
      { path: '/vc', title: '功能總覽' },
      { path: '/vc/apply', title: '憑證申請' },
      { path: '/vc/help', title: '使用說明' },
    ],
  },
  { id: 'news', name: '最新消息', start: '/news', subpages: [] },
  {
    id: 'about',
    name: '關於中心',
    start: '/about',
    subpages: [
      { path: '/about', title: '中心介紹' },
      { path: '/about/architecture', title: '服務架構' },
      { path: '/about/partners', title: '合作單位' },
      { path: '/about/faq', title: '常見問題' },
      { path: '/about/contact', title: '聯絡我們' },
    ],
  },
];

export const RedesignShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const history = useHistory();
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    return localStorage.getItem('dcmc_dark_mode') === 'true';
  });
  const [modal, setModal] = useState<ModalData | null>(null);

  const openModal = (message: string, detail?: string) => {
    setModal({ message, detail });
  };

  const closeModal = () => {
    setModal(null);
  };

  const toggleDarkMode = () => {
    setDarkMode((prev) => {
      const next = !prev;
      localStorage.setItem('dcmc_dark_mode', String(next));
      return next;
    });
  };

  useEffect(() => {
    document.documentElement.style.colorScheme = darkMode ? 'dark' : 'light';
  }, [darkMode]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Determine active group and subpage
  const currentPath = location.pathname;
  let activeGroup = NAV_GROUPS[0];
  if (currentPath.startsWith('/vc')) {
    activeGroup = NAV_GROUPS[1];
  } else if (currentPath.startsWith('/news')) {
    activeGroup = NAV_GROUPS[2];
  } else if (currentPath.startsWith('/about')) {
    activeGroup = NAV_GROUPS[3];
  }

  const currentSubpage = activeGroup.subpages.find(
    (sp) => sp.path === currentPath || (sp.path === '/vc' && currentPath === '/vc/intro')
  );

  const isShellGroup = activeGroup.subpages.length > 0;

  const isBeta = Boolean((window as any)?.env?.BETA);
  const lastUpdated = (window as any)?.env?.LAST_UPDATED;

  return (
    <RedesignContext.Provider value={{ openModal, closeModal, darkMode, toggleDarkMode }}>
      <div
        id="dcmc-v1"
        data-design="digital"
        style={
          {
            '--dc-accent': 'light-dark(#006b70,#76d6d2)',
            '--dc-tint': 'light-dark(#e6f5f4,#183c40)',
            '--dc-gold': 'light-dark(#477773,#a1c9c4)',
            '--dc-radius': '18px',
            colorScheme: darkMode ? 'dark' : 'light',
          } as React.CSSProperties
        }
        aria-label="數位證明管理中心頁面樣版"
      >
        <header>
          <div className="dc-brand">
            <Link to="/" className="dc-brand-link" aria-label="數位證明管理中心 回首頁">
              <div className="dc-mark" aria-hidden="true">
                DC
              </div>
              <div>
                <strong>
                  數位證明管理中心
                  {isBeta && (
                    <>
                      (<span style={{ color: '#e53e3e' }}>測試版</span>)
                    </>
                  )}
                </strong>
                <small>Digital Credential Management Center</small>
              </div>
            </Link>
            <button
              type="button"
              className="dc-button dc-theme-toggle"
              id="dc-theme-toggle"
              data-theme-toggle="true"
              aria-label={darkMode ? '切換淺色模式' : '切換深色模式'}
              aria-pressed={darkMode}
              onClick={toggleDarkMode}
            >
              {darkMode ? '切換淺色模式' : '切換深色模式'}
            </button>
          </div>
          <nav className="dc-nav" aria-label="主選單">
            {NAV_GROUPS.map((group) => {
              const isGroupActive = group.id === activeGroup.id;
              return (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => history.push(group.start)}
                  aria-current={isGroupActive ? 'page' : undefined}
                >
                  {group.name}
                </button>
              );
            })}
          </nav>
        </header>

        <main className="dc-main" id="dc-page" aria-live="polite">
          {isShellGroup ? (
            <>
              <div className="dc-mobile-select">
                <label htmlFor="dc-subpage">{activeGroup.name}</label>
                <select
                  id="dc-subpage"
                  value={currentPath}
                  onChange={(e) => history.push(e.target.value)}
                >
                  {activeGroup.subpages.map((sp) => (
                    <option key={sp.path} value={sp.path}>
                      {sp.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="dc-shell">
                <aside className="dc-side" aria-label={`${activeGroup.name}子選單`}>
                  {activeGroup.subpages.map((sp) => {
                    const isSubActive =
                      sp.path === currentPath ||
                      (sp.path === '/vc' && currentPath === '/vc/intro');
                    return (
                      <button
                        key={sp.path}
                        type="button"
                        onClick={() => history.push(sp.path)}
                        aria-current={isSubActive ? 'page' : undefined}
                      >
                        {sp.title}
                      </button>
                    );
                  })}
                </aside>
                <section className="dc-content">
                  <div className="dc-breadcrumb">
                    首頁 / {activeGroup.name} / {currentSubpage?.title || activeGroup.name}
                  </div>
                  {children}
                </section>
              </div>
            </>
          ) : (
            children
          )}
        </main>

        <footer className="dc-footer">
          <div>
            <strong>數位證明管理中心</strong>
            <br />
            國立成功大學電子證書服務小組
          </div>
          <div>
            <div>
              <a href="mailto:dcert-service@netdb.csie.ncku.edu.tw">
                dcert-service@netdb.csie.ncku.edu.tw
              </a>
            </div>
            {lastUpdated && (
              <div style={{ marginTop: '4px' }}>
                更新日期：{lastUpdated}
              </div>
            )}
          </div>
        </footer>

        {modal && (
          <div
            className="dc-modal-backdrop"
            style={{ display: 'flex' }}
            role="dialog"
            aria-modal="true"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeModal();
            }}
          >
            <div className="dc-modal-card">
              <button
                type="button"
                className="dc-modal-close"
                aria-label="關閉"
                onClick={closeModal}
              >
                &times;
              </button>
              <div className="dc-modal-icon-badge" aria-hidden="true">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <h3>{modal.message}</h3>
              <p>{modal.detail || '相關服務目前正在積極規劃與開發中，敬請期待！'}</p>
              <div className="dc-modal-footer">
                <button
                  type="button"
                  className="dc-button primary"
                  onClick={closeModal}
                >
                  確定
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </RedesignContext.Provider>
  );
};

export default RedesignShell;
